function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import get from 'lodash/get';
import PropTypes from 'prop-types';
import React from 'react';
import ImageZoomViewer from 'react-native-web-image-zoom-viewer';
import { getResponsiveImageUrl } from '@dkrm/general-libs/Utils/imageUtils';
import { getUrlWithOverridenQueries } from '@dkrm/general-libs/Utils/urlUtils';
import { AspectRatioImage, Dimensions, Gradient, ResponsiveImage, Text, View } from '@dkrm/ui-kit-basic';
import { K_DIRECTION_VERTICAL } from '@dkrm/ui-kit-basic/Gradient/constants';
import { NAV_CHEVRON_LEFT, NAV_CHEVRON_RIGHT, NAV_CLOSE } from '@dkrm/ui-kit-basic/Icon/constants';
import colors from '@dkrm/ui-kit-basic/colors';
import { ButtonWithIcon, ModalEnhanced } from '@dkrm/ui-kit-basic/v2';
import styles, { C_BLACK_PURE_30, C_BLACK_PURE_60 } from './styles';
export default class ImageViewer extends React.Component {
  constructor(_props) {
    super(_props);
    // This function is invoked after actionSetCurrentIndex. So currentIndex represents the updated index
    // EXCEPT: when currentIndex is already 0 before button is pressed, actionSetCurrentIndex won't be invoked
    this.handleLeftArrowPress = () => {
      const {
        images
      } = this.props;
      const {
        currentIndex,
        shouldCycle
      } = this.state;
      if (currentIndex === 0) {
        if (shouldCycle) {
          const modifiedIndex = images.length - 1;
          this.setState({
            currentIndex: modifiedIndex
          });
          this.imageZoomViewerRef.current.loadImage(modifiedIndex);
          return;
        }
        this.setState({
          shouldCycle: true
        });
      }
      this.imageZoomViewerRef.current.loadImage(currentIndex);
    };
    // This function is invoked after actionSetCurrentIndex. So currentIndex represents the updated index
    // EXCEPT: when currentIndex is already 0 before button is pressed, actionSetCurrentIndex won't be invoked
    this.handleRightArrowPress = () => {
      const {
        images
      } = this.props;
      const {
        currentIndex,
        shouldCycle
      } = this.state;
      if (currentIndex === images.length - 1) {
        if (shouldCycle) {
          const modifiedIndex = 0;
          this.setState({
            currentIndex: 0
          });
          this.imageZoomViewerRef.current.loadImage(modifiedIndex);
          return;
        }
        this.setState({
          shouldCycle: true
        });
      }
      this.imageZoomViewerRef.current.loadImage(currentIndex);
    };
    this.getModifiedImageUrl = () => {
      const {
        images,
        imageProps,
        widthProportion,
        heightProportion
      } = this.props;
      if (!widthProportion || !heightProportion) return images;
      let imageWidth = Dimensions.get('window').width;
      let imageHeight = imageWidth * heightProportion / widthProportion;
      if (heightProportion > widthProportion) {
        imageHeight = Dimensions.get('window').height;
        imageWidth = imageHeight * widthProportion / heightProportion;
      }
      return images.map(image => {
        const responsiveImageUrl = getResponsiveImageUrl(image.url, imageWidth, imageHeight);
        return {
          ...image,
          url: getUrlWithOverridenQueries(responsiveImageUrl, get(imageProps, 'additionalQuery', {}))
        };
      });
    };
    this.actionSetCurrentIndex = index => {
      this.setState({
        currentIndex: index,
        shouldCycle: false
      });
    };
    this.actionCloseImagePreview = () => {
      const {
        onClose
      } = this.props;
      onClose();
    };
    this.actionToggleShouldShowImageOnly = () => {
      this.setState(state => ({
        shouldShowImageOnly: !state.shouldShowImageOnly
      }));
    };
    this.renderLeftArrow = () => {
      const {
        shouldShowImageOnly
      } = this.state;
      if (shouldShowImageOnly) return null;
      return /*#__PURE__*/React.createElement(ButtonWithIcon, {
        onPress: this.handleLeftArrowPress,
        containerStyle: [styles.arrowContainer, styles.leftArrowContainer],
        theme: "black",
        size: 24,
        name: NAV_CHEVRON_LEFT
      });
    };
    this.renderRightArrow = () => {
      const {
        shouldShowImageOnly
      } = this.state;
      if (shouldShowImageOnly) return null;
      return /*#__PURE__*/React.createElement(ButtonWithIcon, {
        onPress: this.handleRightArrowPress,
        containerStyle: [styles.arrowContainer, styles.rightArrowContainer],
        theme: "black",
        size: 24,
        name: NAV_CHEVRON_RIGHT
      });
    };
    this.renderCloseButton = () => {
      const {
        shouldShowImageOnly
      } = this.state;
      if (shouldShowImageOnly) return null;
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Gradient, {
        colors: [C_BLACK_PURE_30, colors.C_TRANSPARENT],
        style: styles.gradientTop,
        direction: K_DIRECTION_VERTICAL
      }), /*#__PURE__*/React.createElement(ButtonWithIcon, {
        onPress: this.actionCloseImagePreview,
        hoverableViewStyle: styles.closeButtonContainer,
        theme: "white",
        size: 24,
        name: NAV_CLOSE
      }));
    };
    this.renderFooter = () => {
      const {
        images
      } = this.props;
      const {
        currentIndex,
        shouldShowImageOnly
      } = this.state;
      if (shouldShowImageOnly) return null;
      const caption = get(images[currentIndex], 'caption');
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(View, {
        style: styles.footer
      }, /*#__PURE__*/React.createElement(View, {
        style: styles.footerIndicator
      }, /*#__PURE__*/React.createElement(Text, {
        theme: "body4-black"
      }, currentIndex + 1, " / ", images.length)), /*#__PURE__*/React.createElement(Text, {
        theme: "body2-white"
      }, caption)), /*#__PURE__*/React.createElement(Gradient, {
        colors: [colors.C_TRANSPARENT, C_BLACK_PURE_60],
        style: styles.gradientBottom,
        direction: K_DIRECTION_VERTICAL
      }));
    };
    this.renderImage = props => {
      const {
        imageProps,
        widthProportion,
        heightProportion
      } = this.props;
      const {
        width,
        height
      } = props.style;
      if (!widthProportion || !heightProportion) {
        return /*#__PURE__*/React.createElement(AspectRatioImage, _extends({}, props, {
          source: {
            ...props.source,
            height
          }
        }, imageProps));
      }
      return /*#__PURE__*/React.createElement(ResponsiveImage, _extends({}, props, {
        source: {
          ...props.source,
          width
        },
        widthProportion: widthProportion,
        heightProportion: heightProportion
      }, imageProps));
    };
    const {
      index: _index,
      images: _images
    } = _props;
    this.state = {
      currentIndex: _index,
      shouldCycle: _index === 0 || _index === _images.length - 1,
      shouldShowImageOnly: false,
      visible: false
    };
    this.imageZoomViewerRef = /*#__PURE__*/React.createRef();
  }
  static getDerivedStateFromProps(nextProps, prevState) {
    if (prevState.visible !== nextProps.visible) {
      const state = {
        visible: nextProps.visible
      };
      if (nextProps.visible) {
        state.currentIndex = nextProps.index;
      }
      return state;
    }
    return null;
  }
  componentDidUpdate() {
    const {
      images,
      endThreshold,
      onEndReached
    } = this.props;
    const {
      currentIndex
    } = this.state;
    const currProgress = currentIndex / images.length;
    if (endThreshold > 0 && currProgress >= endThreshold) {
      onEndReached();
    }
  }
  renderImageZoomViewer() {
    const {
      viewerProps
    } = this.props;
    const {
      currentIndex
    } = this.state;
    const images = this.getModifiedImageUrl();
    return /*#__PURE__*/React.createElement(ImageZoomViewer, _extends({
      ref: this.imageZoomViewerRef,
      imageUrls: images,
      index: currentIndex,
      onClick: this.actionToggleShouldShowImageOnly,
      onChange: this.actionSetCurrentIndex,
      renderIndicator: () => null,
      renderFooter: this.renderFooter,
      renderArrowRight: this.renderRightArrow,
      renderArrowLeft: this.renderLeftArrow,
      footerContainerStyle: styles.footerContainer,
      saveToLocalByLongPress: false,
      renderImage: this.renderImage
    }, viewerProps));
  }
  render() {
    const {
      visible
    } = this.state;
    return /*#__PURE__*/React.createElement(ModalEnhanced, {
      visible: visible,
      animationType: "fade",
      onBackdropPress: this.actionCloseImagePreview,
      style: styles.modal
    }, /*#__PURE__*/React.createElement(View, {
      style: styles.viewerContainer
    }, this.renderCloseButton(), this.renderImageZoomViewer()));
  }
}
ImageViewer.propTypes = process.env.NODE_ENV !== "production" ? {
  visible: PropTypes.bool.isRequired,
  index: PropTypes.bool,
  images: PropTypes.arrayOf(PropTypes.shape({
    url: PropTypes.string,
    caption: PropTypes.string
  })).isRequired,
  onClose: PropTypes.func.isRequired,
  viewerProps: PropTypes.shape(),
  imageProps: PropTypes.shape(),
  widthProportion: PropTypes.number,
  heightProportion: PropTypes.number,
  endThreshold: PropTypes.number,
  onEndReached: PropTypes.func
} : {};
ImageViewer.defaultProps = {
  index: 0,
  viewerProps: {},
  imageProps: {},
  widthProportion: null,
  heightProportion: null,
  endThreshold: 0,
  onEndReached: () => null
};